/**
 * AdminProfessionalDetail Component
 * Full detail page for viewing and editing a professional/hairdresser
 * URL: /beu-admin-x7k9/professionals/:professionalId
 */

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import './Admin.css';

class AdminProfessionalDetail extends Component {
    constructor(props) {
        super(props);
        this.state = {
            professional: null,
            loading: true,
            error: null,
            isEditing: false,
            editForm: {},
            saving: false,
            saveSuccess: false,
            saveError: null,
            // Employment switch modal
            showSwitchModal: false,
            switchStep: 1,
            switchNewType: null,
            switchSalonCommissionRate: 0,
            switchProductCommissionRate: 0,
            switchVoucherCommissionRate: 0,
            switchEnableSalonCommission: false,
            switchEnableProductCommission: false,
            switchEnableVoucherCommission: false,
            switchBookingStats: null,
            switchBookingStatsLoading: false,
            switchUpdateBookings: 'none',
            switchFromDate: '',
            switchExecuting: false,
            switchError: null,
            switchResult: null
        };
    }

    componentDidMount() {
        this.fetchProfessional();
    }

    fetchProfessional = async () => {
        const { id } = this.props.match.params;
        this.setState({ loading: true, error: null });

        try {
            const response = await adminService.getHairdresserById(id);
            if (response.statusCode === 1) {
                const professional = response.responseData;

                // Backend doesn't populate userId on this endpoint — fetch separately so
                // name/email/phone/avatar render. salon is already populated.
                if (professional.userId && typeof professional.userId === 'string') {
                    try {
                        const userResp = await adminService.getUserById(professional.userId);
                        if (userResp.statusCode === 1 && userResp.responseData) {
                            professional.userId = userResp.responseData;
                        }
                    } catch (userErr) {
                        console.error('Error fetching professional user:', userErr);
                    }
                }

                this.setState({
                    professional,
                    editForm: {
                        jobTitle: professional.jobTitle || '',
                        shortBio: professional.shortBio || '',
                        isApproved: professional.isApproved !== false,
                        isSalonOwner: professional.isSalonOwner || false,
                        employmentTypeId: professional.employmentTypeId || 1,
                        salonCommissionRate: professional.salonCommissionRate || 0,
                        productCommissionRate: professional.productCommissionRate || 0,
                        voucherCommissionRate: professional.voucherCommissionRate || 0
                    },
                    loading: false
                });
            } else {
                this.setState({ error: 'Failed to load professional', loading: false });
            }
        } catch (error) {
            console.error('Error fetching professional:', error);
            this.setState({ error: 'Failed to load professional', loading: false });
        }
    }

    handleEditFormChange = (field, value) => {
        this.setState(prevState => ({
            editForm: {
                ...prevState.editForm,
                [field]: value
            },
            saveSuccess: false,
            saveError: null
        }));
    }

    handleSave = async () => {
        const { professional, editForm } = this.state;
        this.setState({ saving: true, saveError: null, saveSuccess: false });

        try {
            const response = await adminService.updateHairdresser(professional._id, editForm);
            if (response.statusCode === 1) {
                // Refetch to get populated data
                await this.fetchProfessional();
                this.setState({
                    saving: false,
                    isEditing: false,
                    saveSuccess: true
                });
                setTimeout(() => this.setState({ saveSuccess: false }), 3000);
            } else {
                this.setState({ saveError: 'Failed to save changes', saving: false });
            }
        } catch (error) {
            console.error('Error saving professional:', error);
            this.setState({ saveError: 'Failed to save changes', saving: false });
        }
    }

    handleDelete = async () => {
        const { professional } = this.state;
        const name = this.getUserName(professional);
        if (!window.confirm(`Are you sure you want to delete "${name}"?\n\nThis will affect their bookings and services. This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await adminService.deleteHairdresser(professional._id);
            if (response.statusCode === 1) {
                this.props.history.push('/beu-admin-x7k9/professionals');
            } else {
                alert('Failed to delete professional');
            }
        } catch (error) {
            console.error('Error deleting professional:', error);
            alert('Failed to delete professional');
        }
    }

    cancelEdit = () => {
        const { professional } = this.state;
        this.setState({
            isEditing: false,
            editForm: {
                jobTitle: professional.jobTitle || '',
                shortBio: professional.shortBio || '',
                isApproved: professional.isApproved !== false,
                isSalonOwner: professional.isSalonOwner || false,
                employmentTypeId: professional.employmentTypeId || 1,
                salonCommissionRate: professional.salonCommissionRate || 0,
                productCommissionRate: professional.productCommissionRate || 0,
                voucherCommissionRate: professional.voucherCommissionRate || 0
            },
            saveError: null
        });
    }

    getUserObject = (professional) => {
        if (professional.userId && typeof professional.userId === 'object') {
            return professional.userId;
        }
        if (professional.user && typeof professional.user === 'object') {
            return professional.user;
        }
        return null;
    }

    getUserName = (professional) => {
        const user = this.getUserObject(professional);
        if (user) return user.name || 'Unknown';
        return professional.userName || 'Unknown';
    }

    getUserEmail = (professional) => {
        const user = this.getUserObject(professional);
        if (user) return user.email || '';
        return professional.userEmail || '';
    }

    getUserPhone = (professional) => {
        const user = this.getUserObject(professional);
        if (user) return user.phoneNumber || '';
        return professional.userPhoneNumber || professional.userPhone || '';
    }

    getUserAvatar = (professional) => {
        const user = this.getUserObject(professional);
        if (user) return user.localAvatar || user.avatar;
        return professional.userAvatar || professional.userImage?.circleImage;
    }

    getSalonName = (professional) => {
        if (professional.salon && typeof professional.salon === 'object') {
            return professional.salon.salonName || professional.salon.name || 'Unknown Salon';
        }
        return professional.salonName || 'No Salon';
    }

    getInitials = (name) => {
        if (!name) return 'P';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }

    formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    getEmploymentType = (typeId) => {
        switch (typeId) {
            case 1: return 'Employed';
            case 2: return 'Self-Employed';
            default: return 'Unknown';
        }
    }

    // ==================== Employment Switch Modal ====================

    openSwitchModal = () => {
        const { professional } = this.state;
        const currentType = professional.employmentTypeId || 1;
        const newType = currentType === 1 ? 2 : 1;

        this.setState({
            showSwitchModal: true,
            switchStep: 1,
            switchNewType: newType,
            switchSalonCommissionRate: newType === 2 ? (professional.salonCommissionRate || 0) : 0,
            switchProductCommissionRate: newType === 2 ? (professional.productCommissionRate || 0) : 0,
            switchVoucherCommissionRate: newType === 2 ? (professional.voucherCommissionRate || 0) : 0,
            switchEnableSalonCommission: newType === 2,
            switchEnableProductCommission: newType === 2 && (professional.productCommissionRate || 0) > 0,
            switchEnableVoucherCommission: newType === 2 && (professional.voucherCommissionRate || 0) > 0,
            switchBookingStats: null,
            switchUpdateBookings: 'none',
            switchFromDate: '',
            switchExecuting: false,
            switchError: null,
            switchResult: null
        });
    }

    closeSwitchModal = () => {
        this.setState({ showSwitchModal: false });
    }

    switchGoToStep = async (step) => {
        if (step === 2 && !this.state.switchBookingStats) {
            this.setState({ switchBookingStatsLoading: true, switchError: null });
            try {
                const response = await adminService.getHairdresserBookingStats(this.state.professional._id);
                if (response.statusCode === 1) {
                    this.setState({
                        switchBookingStats: response.responseData,
                        switchBookingStatsLoading: false,
                        switchStep: 2
                    });
                } else {
                    this.setState({ switchError: 'Failed to load booking stats', switchBookingStatsLoading: false });
                }
            } catch (error) {
                this.setState({ switchError: 'Failed to load booking stats', switchBookingStatsLoading: false });
            }
            return;
        }
        this.setState({ switchStep: step, switchError: null });
    }

    executeSwitchEmployment = async () => {
        const {
            professional, switchNewType,
            switchSalonCommissionRate, switchProductCommissionRate, switchVoucherCommissionRate,
            switchEnableSalonCommission, switchEnableProductCommission, switchEnableVoucherCommission,
            switchUpdateBookings, switchFromDate
        } = this.state;

        this.setState({ switchExecuting: true, switchError: null });

        try {
            const response = await adminService.switchEmploymentType(professional._id, {
                newEmploymentTypeId: switchNewType,
                salonCommissionRate: switchEnableSalonCommission ? (switchSalonCommissionRate || 0) : 0,
                productCommissionRate: switchEnableProductCommission ? (switchProductCommissionRate || 0) : 0,
                voucherCommissionRate: switchEnableVoucherCommission ? (switchVoucherCommissionRate || 0) : 0,
                updateBookings: switchUpdateBookings,
                fromDate: switchUpdateBookings === 'from_date' ? switchFromDate : null
            });

            if (response.statusCode === 1) {
                this.setState({
                    switchExecuting: false,
                    switchResult: response.responseData
                });
                await this.fetchProfessional();
            } else {
                this.setState({
                    switchError: 'Failed to switch employment type',
                    switchExecuting: false
                });
            }
        } catch (error) {
            this.setState({
                switchError: 'Failed to switch employment type. Please try again.',
                switchExecuting: false
            });
        }
    }

    renderSwitchModal = () => {
        const {
            showSwitchModal, switchStep, professional,
            switchNewType, switchSalonCommissionRate, switchProductCommissionRate,
            switchVoucherCommissionRate, switchEnableSalonCommission,
            switchEnableProductCommission, switchEnableVoucherCommission,
            switchBookingStats, switchBookingStatsLoading,
            switchUpdateBookings, switchFromDate,
            switchExecuting, switchError, switchResult
        } = this.state;

        if (!showSwitchModal) return null;

        const currentTypeName = this.getEmploymentType(professional.employmentTypeId);
        const newTypeName = this.getEmploymentType(switchNewType);

        const radioStyle = (selected) => ({
            display: 'flex', alignItems: 'flex-start', gap: '10px',
            padding: '12px 16px', borderRadius: '8px', cursor: 'pointer',
            border: selected ? '2px solid #4A90D9' : '2px solid #e0e0e0',
            background: selected ? '#f0f7ff' : '#fff'
        });

        const commissionRowStyle = {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 0', borderBottom: '1px solid #f0f0f0'
        };

        return (
            <div className="admin-modal-overlay" onClick={this.closeSwitchModal}>
                <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
                    <div className="admin-modal-header">
                        <h2 style={{ margin: 0, fontSize: '20px' }}>Switch Employment Type</h2>
                        <button className="admin-modal-close" onClick={this.closeSwitchModal}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    <div className="admin-modal-body" style={{ padding: '24px' }}>
                        {/* Step indicator */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                            {[1, 2, 3].map(s => (
                                <div key={s} style={{
                                    flex: 1, height: '4px', borderRadius: '2px',
                                    background: s <= switchStep ? '#4A90D9' : '#e0e0e0'
                                }} />
                            ))}
                        </div>

                        {switchError && (
                            <div style={{ padding: '10px 14px', background: '#ffebee', borderRadius: '8px', marginBottom: '16px', color: '#c62828', fontSize: '14px' }}>
                                {switchError}
                            </div>
                        )}

                        {/* STEP 1: Commission Setup */}
                        {switchStep === 1 && (
                            <div>
                                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '12px 20px', background: '#f5f5f5', borderRadius: '12px' }}>
                                        <span className="admin-badge admin-badge-secondary">{currentTypeName}</span>
                                        <span style={{ color: '#999', fontSize: '18px' }}>&rarr;</span>
                                        <span className="admin-badge admin-badge-info">{newTypeName}</span>
                                    </div>
                                </div>

                                <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>
                                    Commission Rates
                                </h4>
                                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#666' }}>
                                    {switchNewType === 2
                                        ? 'Set commission rates for this self-employed professional.'
                                        : 'This professional is moving to employed. Toggle off commissions to remove them.'}
                                </p>

                                {/* Salon Commission */}
                                <div style={commissionRowStyle}>
                                    <label className="admin-checkbox-label" style={{ margin: 0, gap: '8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={switchEnableSalonCommission}
                                            onChange={e => this.setState({ switchEnableSalonCommission: e.target.checked })}
                                        />
                                        <span>Booking Commission</span>
                                    </label>
                                    {switchEnableSalonCommission && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <input
                                                type="number" min="0" max="100" step="0.5"
                                                value={switchSalonCommissionRate}
                                                onChange={e => this.setState({ switchSalonCommissionRate: parseFloat(e.target.value) || 0 })}
                                                style={{ width: '70px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd', textAlign: 'right' }}
                                            />
                                            <span style={{ color: '#666' }}>%</span>
                                        </div>
                                    )}
                                </div>

                                {/* Product Commission */}
                                <div style={commissionRowStyle}>
                                    <label className="admin-checkbox-label" style={{ margin: 0, gap: '8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={switchEnableProductCommission}
                                            onChange={e => this.setState({ switchEnableProductCommission: e.target.checked })}
                                        />
                                        <span>Product Commission</span>
                                    </label>
                                    {switchEnableProductCommission && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <input
                                                type="number" min="0" max="100" step="0.5"
                                                value={switchProductCommissionRate}
                                                onChange={e => this.setState({ switchProductCommissionRate: parseFloat(e.target.value) || 0 })}
                                                style={{ width: '70px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd', textAlign: 'right' }}
                                            />
                                            <span style={{ color: '#666' }}>%</span>
                                        </div>
                                    )}
                                </div>

                                {/* Voucher Commission */}
                                <div style={{ ...commissionRowStyle, borderBottom: 'none' }}>
                                    <label className="admin-checkbox-label" style={{ margin: 0, gap: '8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={switchEnableVoucherCommission}
                                            onChange={e => this.setState({ switchEnableVoucherCommission: e.target.checked })}
                                        />
                                        <span>Voucher Commission</span>
                                    </label>
                                    {switchEnableVoucherCommission && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <input
                                                type="number" min="0" max="100" step="0.5"
                                                value={switchVoucherCommissionRate}
                                                onChange={e => this.setState({ switchVoucherCommissionRate: parseFloat(e.target.value) || 0 })}
                                                style={{ width: '70px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd', textAlign: 'right' }}
                                            />
                                            <span style={{ color: '#666' }}>%</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* STEP 2: Booking Impact */}
                        {switchStep === 2 && (
                            <div>
                                <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>
                                    Update Existing Bookings
                                </h4>

                                {switchBookingStatsLoading ? (
                                    <div style={{ textAlign: 'center', padding: '32px' }}>
                                        <div className="admin-loading-spinner" style={{ margin: '0 auto 12px' }}></div>
                                        <p style={{ color: '#666', margin: 0 }}>Loading booking data...</p>
                                    </div>
                                ) : switchBookingStats ? (
                                    <div>
                                        <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '12px', marginBottom: '20px', textAlign: 'center' }}>
                                            <div style={{ fontSize: '32px', fontWeight: '700' }}>
                                                {switchBookingStats.totalBookings}
                                            </div>
                                            <div style={{ color: '#666', fontSize: '14px' }}>
                                                total bookings (excl. cancelled)
                                            </div>
                                            {switchBookingStats.earliestBooking && (
                                                <div style={{ color: '#999', fontSize: '12px', marginTop: '4px' }}>
                                                    {new Date(switchBookingStats.earliestBooking).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    {' - '}
                                                    {new Date(switchBookingStats.latestBooking).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </div>
                                            )}
                                        </div>

                                        {switchBookingStats.totalBookings === 0 ? (
                                            <p style={{ color: '#666', fontSize: '14px', textAlign: 'center' }}>
                                                No bookings to update. Commission rates will only apply to future bookings.
                                            </p>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#666' }}>
                                                    Do you want to recalculate commission on existing bookings?
                                                </p>

                                                <label style={radioStyle(switchUpdateBookings === 'none')}>
                                                    <input
                                                        type="radio" name="updateBookings" value="none"
                                                        checked={switchUpdateBookings === 'none'}
                                                        onChange={() => this.setState({ switchUpdateBookings: 'none' })}
                                                        style={{ marginTop: '2px' }}
                                                    />
                                                    <div>
                                                        <div style={{ fontWeight: '500', fontSize: '14px' }}>Don't update existing bookings</div>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>Only apply new rates to future bookings</div>
                                                    </div>
                                                </label>

                                                <label style={radioStyle(switchUpdateBookings === 'all')}>
                                                    <input
                                                        type="radio" name="updateBookings" value="all"
                                                        checked={switchUpdateBookings === 'all'}
                                                        onChange={() => this.setState({ switchUpdateBookings: 'all' })}
                                                        style={{ marginTop: '2px' }}
                                                    />
                                                    <div>
                                                        <div style={{ fontWeight: '500', fontSize: '14px' }}>Update ALL {switchBookingStats.totalBookings} bookings</div>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>Recalculate commission on every booking</div>
                                                    </div>
                                                </label>

                                                <label style={radioStyle(switchUpdateBookings === 'from_date')}>
                                                    <input
                                                        type="radio" name="updateBookings" value="from_date"
                                                        checked={switchUpdateBookings === 'from_date'}
                                                        onChange={() => this.setState({ switchUpdateBookings: 'from_date' })}
                                                        style={{ marginTop: '2px' }}
                                                    />
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: '500', fontSize: '14px' }}>Update bookings from a specific date</div>
                                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: switchUpdateBookings === 'from_date' ? '8px' : 0 }}>
                                                            Keep historic bookings unchanged
                                                        </div>
                                                        {switchUpdateBookings === 'from_date' && (
                                                            <input
                                                                type="date"
                                                                value={switchFromDate}
                                                                onChange={e => this.setState({ switchFromDate: e.target.value })}
                                                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
                                                            />
                                                        )}
                                                    </div>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {/* STEP 3: Confirm & Execute */}
                        {switchStep === 3 && (
                            <div>
                                {switchResult ? (
                                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>&#10003;</div>
                                        <h3 style={{ margin: '0 0 8px 0', color: '#2e7d32' }}>Employment Type Switched</h3>
                                        <p style={{ color: '#666', margin: '0 0 4px 0' }}>
                                            Now set to <strong>{newTypeName}</strong>
                                        </p>
                                        {switchResult.bookingsUpdated > 0 && (
                                            <p style={{ color: '#666', margin: 0 }}>
                                                {switchResult.bookingsUpdated} booking{switchResult.bookingsUpdated !== 1 ? 's' : ''} updated
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>
                                            Confirm Changes
                                        </h4>

                                        <div style={{ background: '#f5f5f5', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                                                <span style={{ color: '#666', fontSize: '14px' }}>Employment Type</span>
                                                <span style={{ fontWeight: '600', fontSize: '14px' }}>{currentTypeName} &rarr; {newTypeName}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                                                <span style={{ color: '#666', fontSize: '14px' }}>Booking Commission</span>
                                                <span style={{ fontWeight: '600', fontSize: '14px' }}>
                                                    {switchEnableSalonCommission ? `${switchSalonCommissionRate}%` : 'Off'}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                                                <span style={{ color: '#666', fontSize: '14px' }}>Product Commission</span>
                                                <span style={{ fontWeight: '600', fontSize: '14px' }}>
                                                    {switchEnableProductCommission ? `${switchProductCommissionRate}%` : 'Off'}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                                                <span style={{ color: '#666', fontSize: '14px' }}>Voucher Commission</span>
                                                <span style={{ fontWeight: '600', fontSize: '14px' }}>
                                                    {switchEnableVoucherCommission ? `${switchVoucherCommissionRate}%` : 'Off'}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                                <span style={{ color: '#666', fontSize: '14px' }}>Booking Updates</span>
                                                <span style={{ fontWeight: '600', fontSize: '14px' }}>
                                                    {switchUpdateBookings === 'none' && 'None (future only)'}
                                                    {switchUpdateBookings === 'all' && `All ${switchBookingStats?.totalBookings || 0} bookings`}
                                                    {switchUpdateBookings === 'from_date' && `From ${new Date(switchFromDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                                                </span>
                                            </div>
                                        </div>

                                        {switchUpdateBookings !== 'none' && (
                                            <div style={{ padding: '10px 14px', background: '#fff3e0', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#e65100' }}>
                                                This will recalculate commission amounts on existing bookings. This action cannot be easily undone.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="admin-modal-footer" style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 24px', borderTop: '1px solid #eee' }}>
                        {switchResult ? (
                            <div style={{ width: '100%', textAlign: 'right' }}>
                                <button className="admin-btn admin-btn-primary" onClick={this.closeSwitchModal}>
                                    Done
                                </button>
                            </div>
                        ) : (
                            <>
                                <div>
                                    {switchStep > 1 && (
                                        <button
                                            className="admin-btn admin-btn-secondary"
                                            onClick={() => this.switchGoToStep(switchStep - 1)}
                                            disabled={switchExecuting}
                                        >
                                            Back
                                        </button>
                                    )}
                                    {switchStep === 1 && (
                                        <button className="admin-btn admin-btn-secondary" onClick={this.closeSwitchModal}>
                                            Cancel
                                        </button>
                                    )}
                                </div>
                                <div>
                                    {switchStep < 3 && (
                                        <button
                                            className="admin-btn admin-btn-primary"
                                            onClick={() => this.switchGoToStep(switchStep + 1)}
                                            disabled={switchBookingStatsLoading || (switchStep === 2 && switchUpdateBookings === 'from_date' && !switchFromDate)}
                                        >
                                            {switchBookingStatsLoading ? 'Loading...' : 'Next'}
                                        </button>
                                    )}
                                    {switchStep === 3 && (
                                        <button
                                            className="admin-btn admin-btn-primary"
                                            onClick={this.executeSwitchEmployment}
                                            disabled={switchExecuting}
                                        >
                                            {switchExecuting ? 'Processing...' : 'Confirm & Execute'}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { professional, loading, error, isEditing, editForm, saving, saveSuccess, saveError } = this.state;

        if (loading) {
            return (
                <AdminWrapper>
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <div className="admin-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                        <p style={{ color: '#666' }}>Loading professional...</p>
                    </div>
                </AdminWrapper>
            );
        }

        if (error || !professional) {
            return (
                <AdminWrapper>
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error || 'Professional not found'}</p>
                        <Link to="/beu-admin-x7k9/professionals" className="admin-btn admin-btn-secondary">
                            Back to Professionals
                        </Link>
                    </div>
                </AdminWrapper>
            );
        }

        const name = this.getUserName(professional);
        const email = this.getUserEmail(professional);
        const phone = this.getUserPhone(professional);
        const avatar = this.getUserAvatar(professional);
        const salonName = this.getSalonName(professional);

        return (
            <AdminWrapper>
                {/* Breadcrumb */}
                <div style={{ marginBottom: '24px' }}>
                    <Link to="/beu-admin-x7k9/professionals" style={{ color: '#666', textDecoration: 'none', fontSize: '14px' }}>
                        ← Back to Professionals
                    </Link>
                </div>

                {/* Success/Error Messages */}
                {saveSuccess && (
                    <div style={{
                        padding: '12px 16px',
                        background: '#e8f5e9',
                        borderRadius: '8px',
                        marginBottom: '24px',
                        color: '#2e7d32',
                        fontWeight: '500'
                    }}>
                        ✓ Changes saved successfully
                    </div>
                )}
                {saveError && (
                    <div style={{
                        padding: '12px 16px',
                        background: '#ffebee',
                        borderRadius: '8px',
                        marginBottom: '24px',
                        color: '#c62828',
                        fontWeight: '500'
                    }}>
                        ✕ {saveError}
                    </div>
                )}

                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: '32px',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        {avatar ? (
                            <img
                                src={avatar}
                                alt={name}
                                style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }}
                            />
                        ) : (
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '28px',
                                fontWeight: '600'
                            }}>
                                {this.getInitials(name)}
                            </div>
                        )}
                        <div>
                            <h1 style={{ margin: '0 0 4px 0', fontSize: '28px', fontWeight: '700' }}>
                                {name}
                            </h1>
                            <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
                                {professional.jobTitle || 'Professional'} at {salonName}
                            </p>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <span className={`admin-badge ${professional.isApproved !== false ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                                    {professional.isApproved !== false ? 'Active' : 'Inactive'}
                                </span>
                                {professional.isSalonOwner && (
                                    <span className="admin-badge admin-badge-info">Salon Owner</span>
                                )}
                                <span className="admin-badge admin-badge-secondary">
                                    {this.getEmploymentType(professional.employmentTypeId)}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        {!isEditing ? (
                            <>
{professional.salon && (
                                    <a
                                        href={`/salon/${professional.salon.slug || professional.salon._id}/professional/${professional.userId?._id || professional.userId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="admin-btn admin-btn-secondary"
                                    >
                                        View Booking Page
                                    </a>
                                )}
                                <button
                                    className="admin-btn admin-btn-primary"
                                    onClick={() => this.setState({ isEditing: true })}
                                >
                                    Edit Professional
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="admin-btn admin-btn-secondary"
                                    onClick={this.cancelEdit}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="admin-btn admin-btn-primary"
                                    onClick={this.handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Content Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
                    {/* Main Content */}
                    <div>
                        {/* Professional Information */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Professional Information</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                {isEditing ? (
                                    <div className="admin-form">
                                        <div className="admin-form-group">
                                            <label>Job Title</label>
                                            <input
                                                type="text"
                                                value={editForm.jobTitle}
                                                onChange={(e) => this.handleEditFormChange('jobTitle', e.target.value)}
                                                placeholder="e.g., Senior Stylist"
                                            />
                                        </div>
                                        <div className="admin-form-group">
                                            <label>Bio</label>
                                            <textarea
                                                value={editForm.shortBio}
                                                onChange={(e) => this.handleEditFormChange('shortBio', e.target.value)}
                                                placeholder="Short biography..."
                                                rows="4"
                                            />
                                        </div>
                                        <div className="admin-form-group">
                                            <label>Employment Type</label>
                                            <select
                                                value={editForm.employmentTypeId}
                                                onChange={(e) => this.handleEditFormChange('employmentTypeId', parseInt(e.target.value))}
                                            >
                                                <option value={1}>Employed</option>
                                                <option value={2}>Self-Employed</option>
                                            </select>
                                        </div>
                                        <div className="admin-form-row">
                                            <div className="admin-form-group">
                                                <label className="admin-checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={editForm.isApproved}
                                                        onChange={(e) => this.handleEditFormChange('isApproved', e.target.checked)}
                                                    />
                                                    <span>Active / Approved</span>
                                                </label>
                                            </div>
                                            <div className="admin-form-group">
                                                <label className="admin-checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={editForm.isSalonOwner}
                                                        onChange={(e) => this.handleEditFormChange('isSalonOwner', e.target.checked)}
                                                    />
                                                    <span>Salon Owner</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="admin-detail-grid">
                                        <div className="admin-detail-row">
                                            <label>Name</label>
                                            <span>{name}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Email</label>
                                            <span>{email || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Phone</label>
                                            <span>{phone || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Job Title</label>
                                            <span>{professional.jobTitle || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Employment Type</label>
                                            <span>{this.getEmploymentType(professional.employmentTypeId)}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Status</label>
                                            <span className={`admin-badge ${professional.isApproved !== false ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                                                {professional.isApproved !== false ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Salon Owner</label>
                                            <span className={`admin-badge ${professional.isSalonOwner ? 'admin-badge-info' : 'admin-badge-secondary'}`}>
                                                {professional.isSalonOwner ? 'Yes' : 'No'}
                                            </span>
                                        </div>
                                        {professional.shortBio && (
                                            <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                                                <label>Bio</label>
                                                <span style={{ color: '#666' }}>{professional.shortBio}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Commission Rates */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Commission Rates</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                {isEditing ? (
                                    <div className="admin-form">
                                        <div className="admin-form-row">
                                            <div className="admin-form-group">
                                                <label>Salon Commission (%)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={editForm.salonCommissionRate}
                                                    onChange={(e) => this.handleEditFormChange('salonCommissionRate', parseFloat(e.target.value) || 0)}
                                                />
                                            </div>
                                            <div className="admin-form-group">
                                                <label>Product Commission (%)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={editForm.productCommissionRate}
                                                    onChange={(e) => this.handleEditFormChange('productCommissionRate', parseFloat(e.target.value) || 0)}
                                                />
                                            </div>
                                        </div>
                                        <div className="admin-form-group">
                                            <label>Voucher Commission (%)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={editForm.voucherCommissionRate}
                                                onChange={(e) => this.handleEditFormChange('voucherCommissionRate', parseFloat(e.target.value) || 0)}
                                                style={{ maxWidth: '200px' }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
                                        <div style={{ textAlign: 'center', padding: '16px', background: '#f5f5f5', borderRadius: '12px' }}>
                                            <div style={{ fontSize: '28px', fontWeight: '700' }}>
                                                {professional.salonCommissionRate || 0}%
                                            </div>
                                            <div style={{ color: '#666', fontSize: '14px' }}>Salon Commission</div>
                                        </div>
                                        <div style={{ textAlign: 'center', padding: '16px', background: '#f5f5f5', borderRadius: '12px' }}>
                                            <div style={{ fontSize: '28px', fontWeight: '700' }}>
                                                {professional.productCommissionRate || 0}%
                                            </div>
                                            <div style={{ color: '#666', fontSize: '14px' }}>Product Commission</div>
                                        </div>
                                        <div style={{ textAlign: 'center', padding: '16px', background: '#f5f5f5', borderRadius: '12px' }}>
                                            <div style={{ fontSize: '28px', fontWeight: '700' }}>
                                                {professional.voucherCommissionRate || 0}%
                                            </div>
                                            <div style={{ color: '#666', fontSize: '14px' }}>Voucher Commission</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Linked Salon */}
                        {professional.salon && (
                            <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                                <div className="admin-table-header">
                                    <h3 className="admin-table-title">Linked Salon</h3>
                                </div>
                                <div style={{ padding: '24px' }}>
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ fontWeight: '600', fontSize: '18px' }}>{salonName}</div>
                                        {typeof professional.salon === 'object' && professional.salon.address && (
                                            <div style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
                                                {typeof professional.salon.address === 'object'
                                                    ? professional.salon.address.city
                                                    : professional.salon.address}
                                            </div>
                                        )}
                                    </div>
                                    {typeof professional.salon === 'object' && professional.salon._id && (
                                        <Link
                                            to={`/beu-admin-x7k9/businesses/${professional.salon._id}`}
                                            className="admin-btn admin-btn-secondary"
                                        >
                                            View Salon →
                                        </Link>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div>
                        {/* System Info */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">System Information</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div className="admin-detail-grid">
                                    <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                        <label>Professional ID</label>
                                        <span className="admin-id-text">{professional._id}</span>
                                    </div>
                                    {professional.userId && (
                                        <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                            <label>User ID</label>
                                            <span className="admin-id-text">
                                                {typeof professional.userId === 'object' ? professional.userId._id : professional.userId}
                                            </span>
                                        </div>
                                    )}
                                    <div className="admin-detail-row">
                                        <label>Created</label>
                                        <span>{this.formatDateTime(professional.createdTime)}</span>
                                    </div>
                                    <div className="admin-detail-row">
                                        <label>Last Updated</label>
                                        <span>{this.formatDateTime(professional.updatedTime)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="admin-table-container">
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Quick Actions</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <button
                                        className="admin-btn admin-btn-primary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={this.openSwitchModal}
                                    >
                                        Switch to {professional.employmentTypeId === 2 ? 'Employed' : 'Self-Employed'}
                                    </button>
                                    <button
                                        className="admin-btn admin-btn-secondary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={() => {
                                            const newStatus = !(professional.isApproved !== false);
                                            this.handleEditFormChange('isApproved', newStatus);
                                            this.setState({ isEditing: false }, this.handleSave);
                                        }}
                                    >
                                        {professional.isApproved !== false ? 'Deactivate Professional' : 'Activate Professional'}
                                    </button>
                                    {professional.userId && (
                                        <Link
                                            to={`/beu-admin-x7k9/users/${typeof professional.userId === 'object' ? professional.userId._id : professional.userId}`}
                                            className="admin-btn admin-btn-secondary"
                                            style={{ width: '100%', justifyContent: 'center', textDecoration: 'none' }}
                                        >
                                            View User Account
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Danger Zone */}
                <div className="admin-danger-zone" style={{ marginTop: '48px' }}>
                    <div className="admin-table-container" style={{ border: '1px solid #ff4444' }}>
                        <div className="admin-table-header" style={{ background: '#fff5f5' }}>
                            <h3 className="admin-table-title" style={{ color: '#cc0000' }}>Danger Zone</h3>
                        </div>
                        <div style={{ padding: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                                <div>
                                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>Delete this professional</div>
                                    <div style={{ color: '#666', fontSize: '14px' }}>
                                        Once deleted, this professional's profile and associated data will be permanently removed.
                                    </div>
                                </div>
                                <button
                                    className="admin-btn admin-btn-danger"
                                    onClick={this.handleDelete}
                                >
                                    Delete Professional
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Employment Switch Modal */}
                {this.renderSwitchModal()}
            </AdminWrapper>
        );
    }
}

export default AdminProfessionalDetail;
